import { MicelioTrackDevicesMetaData } from "./PanelDef";
import React from "react";

import { Tab } from "semantic-ui-react";
import {
  PanelEditComponent,
  EditComponentProps,
  PartialMetaData,
} from "../../PanelDef";
import {
  EditMetaRow,
  EditMetaRoot,
  filterNumericalTypeColumnOptions,
  EditPanelFormContainer,
  EditAnimatedMetaInput,
  EditAnimatedMetaDropdown,
  StyledInputDiv,
  ThinDivider,
} from "../../util";

export type MicelioEditTrackDevicesMetaState = {
  table: string;
  latitude_column: string;
  longitude_column: string;
  error: boolean;
};

export class MicelioEditTrackDevicesMeta extends PanelEditComponent<
  MicelioTrackDevicesMetaData,
  MicelioEditTrackDevicesMetaState
> {
  titleRef = React.createRef<HTMLInputElement>();
  descriptionRef = React.createRef<HTMLInputElement>();
  deviceDashboardIdRef = React.createRef<HTMLInputElement>();

  constructor(props: EditComponentProps<MicelioTrackDevicesMetaData>) {
    super(props);

    if (props.panelMeta) {
      this.state = {
        table: props.panelMeta.table,
        latitude_column: props.panelMeta.latitude_column,
        longitude_column: props.panelMeta.longitude_column,
        error: false,
      };
    } else {
      this.state = {
        table: "",
        latitude_column: "",
        longitude_column: "",
        error: false,
      };
    }
  }

  getPanelMeta(type): PartialMetaData<MicelioTrackDevicesMetaData> {
    const meta: MicelioTrackDevicesMetaData = {
      type: "micelio_track_devices",
      id: this.props.panelMeta.id,
      title: this.titleRef.current?.value || "",
      description: this.descriptionRef.current?.value || "",
      table: this.state.table || "",
      latitude_column: this.state.latitude_column || "",
      longitude_column: this.state.longitude_column || "",
      device_dashboard_id: this.deviceDashboardIdRef.current?.value || "",
      trafficLayerFlag: false,
      satelliteLayerFlag: false,
    };

    return {
      meta: meta,
      complete: this.isValidPanelMeta(meta, type),
    };
  }

  isValidPanelMeta(meta: MicelioTrackDevicesMetaData, type?: string) {
    const isValid =
      !!meta.table && !!meta.latitude_column && !!meta.longitude_column;

    // type is used here to differentiate between submit and refresh in edit mode
    if (!isValid && type === "submit") {
      this.setState({ error: true });
    } else if (type === "submit") {
      this.setState({ error: false });
    }

    return isValid;
  }

  setTable(_event, data) {
    this.setState({
      table: data.value,
      latitude_column: "",
      longitude_column: "",
    });
  }

  setLatitudeColumn(_event, data) {
    this.setState({
      latitude_column: data.value,
    });
  }

  setLongitudeColumn(_event, data) {
    this.setState({
      longitude_column: data.value,
    });
  }

  render() {
    const title = this.props.panelMeta.title;
    const description = this.props.panelMeta.description;
    const deviceDashboardId = this.props.panelMeta.device_dashboard_id;

    const tableOptions = Object.keys(this.props.tables).map((t) => {
      return {
        key: t,
        text: t,
        value: t,
      };
    });

    let latitudeColumnOptions: Array<{
      key: string;
      value: string;
      text: string;
    }> = [];
    let longitudeColumnOptions: Array<{
      key: string;
      value: string;
      text: string;
    }> = [];

    if (!!this.state.table) {
      latitudeColumnOptions = filterNumericalTypeColumnOptions(
        this.props.tables[this.state.table]
      ).map((f: { name: string; type: string }) => {
        return {
          key: f.name,
          text: f.name,
          value: f.name,
        };
      });
    }

    if (!!this.state.table) {
      longitudeColumnOptions = filterNumericalTypeColumnOptions(
        this.props.tables[this.state.table]
      ).map((f: { name: string; type: string }) => {
        return {
          key: f.name,
          text: f.name,
          value: f.name,
        };
      });
    }

    const panes = [
      {
        menuItem: "General",
        pane: (
          <Tab.Pane key={"general"}>
            <EditPanelFormContainer>
              <div style={{ width: "100%", marginTop: "16px" }} />
              <EditMetaRow>
                <StyledInputDiv width="48%">
                  <EditAnimatedMetaInput
                    defaultRef={this.titleRef}
                    defaultValue={title}
                    label="Title"
                    error={this.state.error && !this.titleRef.current?.value}
                  />
                </StyledInputDiv>
                <StyledInputDiv width="48%">
                  <EditAnimatedMetaInput
                    defaultRef={this.descriptionRef}
                    defaultValue={description}
                    label="Description"
                  />
                </StyledInputDiv>
              </EditMetaRow>
              <ThinDivider />
              <EditMetaRow>
                <StyledInputDiv width="50%" marginTop="10px">
                  <EditAnimatedMetaDropdown
                    placeholder="Select Stream"
                    text={this.state.table || "Select Stream"}
                    fluid
                    search
                    selection
                    options={tableOptions}
                    onChange={this.setTable.bind(this)}
                    defaultValue={this.state.table}
                    value={this.state.table}
                    elementID={`tableMicelioTrackDevices`}
                    error={this.state.error && !this.state.table}
                  />
                </StyledInputDiv>
              </EditMetaRow>

              <EditMetaRow>
                <StyledInputDiv width="48%" marginTop="10px">
                  <EditAnimatedMetaDropdown
                    placeholder="Latitude Column"
                    text={this.state.latitude_column || "Latitude Column"}
                    fluid
                    search
                    selection
                    disabled={this.state.table === ""}
                    options={latitudeColumnOptions}
                    onChange={this.setLatitudeColumn.bind(this)}
                    defaultValue={this.state.latitude_column || ""}
                    value={this.state.latitude_column || ""}
                    elementID={`micelioLatitudeColumn`}
                    error={this.state.error && !this.state.latitude_column}
                  />
                </StyledInputDiv>

                <StyledInputDiv width="48%" marginTop="10px">
                  <EditAnimatedMetaDropdown
                    placeholder="Longitude Column"
                    text={this.state.longitude_column || "Longitude Column"}
                    fluid
                    search
                    selection
                    disabled={this.state.table === ""}
                    options={longitudeColumnOptions}
                    onChange={this.setLongitudeColumn.bind(this)}
                    defaultValue={this.state.longitude_column || ""}
                    value={this.state.longitude_column || ""}
                    elementID={`micelioLongitudeColumn`}
                    error={this.state.error && !this.state.longitude_column}
                  />
                </StyledInputDiv>
              </EditMetaRow>

              <ThinDivider />

              <EditMetaRow>
                <StyledInputDiv width="48%" marginTop="10px">
                  <EditAnimatedMetaInput
                    defaultRef={this.deviceDashboardIdRef}
                    defaultValue={deviceDashboardId}
                    label="Device dashboard ID"
                  />
                </StyledInputDiv>
              </EditMetaRow>
            </EditPanelFormContainer>
          </Tab.Pane>
        ),
      },
    ];

    return (
      <EditMetaRoot>
        <Tab menu={{}} panes={panes} renderActiveOnly={false} />
      </EditMetaRoot>
    );
  }
}
